import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Calculator, RefreshCw } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, shiftStatus, PAGE, MONEY, SHIFT } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import {
  addShiftAdjustment,
  confirmCashDrop,
  fetchShiftReport,
  fetchShopCashiers,
  reconcileShift,
  rejectCashDrop,
  requestShiftRecount,
} from '../services/api';
import type { CashierShiftReport, ShopCashier } from '../types';

function formatMoney(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  return `$${Number(value).toFixed(2)}`;
}

export default function ReconciliationPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { shiftId } = useParams();
  const id = shiftId ? decodeURIComponent(shiftId) : '';

  const [report, setReport] = useState<CashierShiftReport | null>(null);
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolution, setResolution] = useState('');
  const [managerNote, setManagerNote] = useState('');
  const [recountNote, setRecountNote] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const cashierName = useMemo(() => {
    if (!report?.shift) return '—';
    return cashiers.find((c) => c.id === report.shift.cashierId)?.name || report.shift.cashierId;
  }, [report, cashiers]);

  const canReconcile = useMemo(() => {
    const status = report?.shift?.status;
    return ['pending_reconciliation', 'pending_manager_review', 'suspended', 'auto_locked'].includes(status || '');
  }, [report]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [reportRes, cashierRes] = await Promise.all([
        fetchShiftReport(id),
        fetchShopCashiers(),
      ]);
      if (!reportRes.success) throw new Error(reportRes.message || 'Failed to load report');
      setReport(reportRes.data || null);
      if (cashierRes.success) setCashiers(cashierRes.data || []);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onReconcile = async () => {
    if (!id) return;
    if (!window.confirm('Finalize reconciliation for this shift?')) return;
    setSubmitting(true);
    try {
      const res = await reconcileShift(id, {
        resolution: resolution.trim() || undefined,
        managerNote: managerNote.trim() || undefined,
        finalize: true,
      });
      if (!res.success) throw new Error(res.message || 'Reconcile failed');
      toast.success('Shift reconciled');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reconcile failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onRecount = async () => {
    if (!id) return;
    if (!window.confirm('Request Cashier to recount closing cash? Shift will reopen.')) return;
    setSubmitting(true);
    try {
      const res = await requestShiftRecount(id, recountNote.trim() ? { note: recountNote.trim() } : undefined);
      if (!res.success) throw new Error(res.message || 'Recount request failed');
      toast.success('Recount requested — shift reopened');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Recount request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onAddAdjustment = async () => {
    if (!id) return;
    const amount = Number(adjAmount);
    if (!Number.isFinite(amount)) {
      toast.error('Enter a valid adjustment amount');
      return;
    }
    if (!adjReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addShiftAdjustment(id, { amount, reason: adjReason.trim() });
      if (!res.success) throw new Error(res.message || 'Adjustment failed');
      toast.success('Adjustment added');
      setAdjAmount('');
      setAdjReason('');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Adjustment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmDrop = async (dropId: string) => {
    setActionId(dropId);
    try {
      const res = await confirmCashDrop(dropId);
      if (!res.success) throw new Error(res.message || 'Confirm failed');
      toast.success('Cash drop confirmed');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Confirm failed');
    } finally {
      setActionId(null);
    }
  };

  const onRejectDrop = async (dropId: string) => {
    const note = window.prompt('Rejection reason (optional):') ?? '';
    setActionId(dropId);
    try {
      const res = await rejectCashDrop(dropId, note.trim() ? { note: note.trim() } : undefined);
      if (!res.success) throw new Error(res.message || 'Reject failed');
      toast.success('Cash drop rejected');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reject failed');
    } finally {
      setActionId(null);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.shiftReconciliation}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Review cash movement, resolve discrepancies and finalize the shift.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => navigate(`/shifts/${encodeURIComponent(id)}`)}
            >
              ← Shift detail
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => void load()}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {!id ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Invalid shift id.
          </div>
        ) : loading && !report ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Loading…
          </div>
        ) : !report ? (
          <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
            Report not found.{' '}
            <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => navigate('/shift-reports')}>
              Go to reports
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Cashier</div>
                <div className="mt-1 text-lg font-bold text-stone-900">{cashierName}</div>
                <div className="mt-0.5 text-xs text-stone-600">{shiftStatus(report.shift.status).label}</div>
              </div>
              <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Transactions</div>
                <div className="mt-1 text-lg font-bold text-stone-900">{report.summary.totalTransactions}</div>
                <div className="mt-0.5 text-xs text-stone-600">
                  Deposits {formatMoney(report.summary.totalDeposits)} · Redemptions {formatMoney(report.summary.totalRedemptions)}
                </div>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-blue-700">Expected closing</div>
                <div className="mt-1 text-lg font-bold text-blue-900">
                  {formatMoney(report.reconciliation.expectedClosingCash)}
                </div>
              </div>
              <div
                className={`rounded-xl border p-4 ${
                  Math.abs(Number(report.reconciliation.discrepancy || 0)) > 0
                    ? 'border-rose-200 bg-rose-50'
                    : 'border-emerald-200 bg-emerald-50'
                }`}
              >
                <div className="text-xs font-medium uppercase tracking-wide opacity-80">Discrepancy</div>
                <div className="mt-1 text-lg font-bold">{formatMoney(report.reconciliation.discrepancy)}</div>
                <div className="mt-0.5 text-xs opacity-80">
                  Counted: {formatMoney(report.reconciliation.actualClosingCash)}
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-stone-200 bg-white lg:col-span-2">
                <div className="border-b border-stone-200 px-4 py-3">
                  <h2 className="flex items-center gap-2 text-base font-semibold">
                    <Calculator className="h-4 w-4 text-stone-500" />
                    Cash summary
                  </h2>
                </div>
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <div className="space-y-2 text-sm">
                    {[
                      ['Opening cash', formatMoney(report.reconciliation.openingCash)],
                      [MONEY.counterDeposits, formatMoney(report.summary.totalCashIn)],
                      [MONEY.counterRedemptions, formatMoney(report.summary.totalCashOut)],
                      ['Confirmed drops', formatMoney(report.summary.totalCashDrops)],
                      ['Adjustments', formatMoney(report.summary.approvedAdjustments)],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="flex items-center justify-between gap-2 border-b border-stone-100 pb-2">
                        <span className="text-stone-600">{label}</span>
                        <strong className="text-stone-900">{value}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 text-sm">
                    {[
                      ['Expected closing', formatMoney(report.reconciliation.expectedClosingCash)],
                      [SHIFT.countedCash, formatMoney(report.reconciliation.actualClosingCash)],
                      ['Discrepancy', formatMoney(report.reconciliation.discrepancy)],
                      ['Shift date', report.shift.shiftDate || '—'],
                      ['Closed at', formatInTimezone(report.shift.closedAt, timezone)],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="flex items-center justify-between gap-2 border-b border-stone-100 pb-2">
                        <span className="text-stone-600">{label}</span>
                        <strong className="text-stone-900">{value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {canReconcile ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h3 className="text-sm font-semibold text-amber-900">Your actions</h3>
                  <p className="mt-1 text-xs text-amber-800">Resolve and finalize this shift.</p>
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-stone-700">Resolution</label>
                      <input
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
                        placeholder="e.g. Verified with Cashier"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-stone-700">Note to the Cashier</label>
                      <textarea
                        value={managerNote}
                        onChange={(e) => setManagerNote(e.target.value)}
                        rows={2}
                        className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
                        placeholder="Optional notes…"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={submitting}
                      className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                      onClick={() => void onReconcile()}
                    >
                      {submitting ? 'Processing…' : 'Finalize reconciliation'}
                    </button>
                    <div className="border-t border-amber-200 pt-3">
                      <label className="text-xs font-medium text-stone-700">Recount note</label>
                      <input
                        value={recountNote}
                        onChange={(e) => setRecountNote(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500"
                        placeholder="Reason for recount…"
                      />
                      <button
                        type="button"
                        disabled={submitting}
                        className="mt-2 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50 disabled:opacity-60"
                        onClick={() => void onRecount()}
                      >
                        Request recount
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <h3 className="text-sm font-semibold text-emerald-900">Reconciliation status</h3>
                  <p className="mt-2 text-sm text-emerald-800">
                    This shift has status <strong>{shiftStatus(report.shift.status).label}</strong>.
                    {report.shift.status.startsWith('completed')
                      ? ' No further action required.'
                      : ' Reconciliation actions are not available for this status.'}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-stone-200 bg-white p-4">
              <h3 className="text-sm font-semibold">Add adjustment</h3>
              <p className="text-xs text-stone-500">Adjust expected closing cash with a documented reason.</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-medium text-stone-600">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjAmount}
                    onChange={(e) => setAdjAmount(e.target.value)}
                    className="mt-1 block w-32 rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="min-w-[200px] flex-1">
                  <label className="text-xs font-medium text-stone-600">Reason</label>
                  <input
                    value={adjReason}
                    onChange={(e) => setAdjReason(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    placeholder="Reason for adjustment…"
                  />
                </div>
                <button
                  type="button"
                  disabled={submitting}
                  className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50 disabled:opacity-60"
                  onClick={() => void onAddAdjustment()}
                >
                  Add adjustment
                </button>
              </div>
            </div>

            {report.adjustments.length > 0 && (
              <div className="mt-4 rounded-xl border border-stone-200 bg-white">
                <div className="border-b border-stone-200 px-4 py-3">
                  <h3 className="text-sm font-semibold">Adjustments</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {report.adjustments.map((adj) => (
                    <div key={adj.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                      <span className="text-stone-700">{adj.reason}</span>
                      <strong className="text-stone-900">{formatMoney(adj.amount)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.cashDrops.length > 0 && (
              <div className="mt-4 rounded-xl border border-stone-200 bg-white">
                <div className="border-b border-stone-200 px-4 py-3">
                  <h3 className="text-sm font-semibold">Cash drops</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {report.cashDrops.map((drop) => (
                    <div key={drop.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-semibold">{formatMoney(drop.totalAmount)}</div>
                        <div className="text-xs capitalize text-stone-600">
                          {drop.status.replace(/_/g, ' ')} · {formatInTimezone(drop.createdAt, timezone)}
                        </div>
                      </div>
                      {drop.status === 'pending_receiver_confirmation' && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={actionId === drop.id}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                            onClick={() => void onConfirmDrop(drop.id)}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            disabled={actionId === drop.id}
                            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                            onClick={() => void onRejectDrop(drop.id)}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.transactions.length > 0 && (
              <div className="mt-4 rounded-xl border border-stone-200 bg-white">
                <div className="border-b border-stone-200 px-4 py-3">
                  <h3 className="text-sm font-semibold">Transactions ({report.transactions.length})</h3>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
                  {report.transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                      <div>
                        <span className="font-medium capitalize text-stone-900">{tx.type.replace(/_/g, ' ')}</span>
                        <span className="ml-2 text-xs text-stone-500">{formatInTimezone(tx.createdAt, timezone)}</span>
                      </div>
                      <strong className="text-stone-900">{formatMoney(tx.amount)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
